import { TranslocoService } from '@jsverse/transloco';
import {
  catchError,
  filter,
  lastValueFrom,
  of,
  switchMap,
  take,
  tap,
  timeout
} from 'rxjs';
import { computed, DOCUMENT, Injector } from '@angular/core';
import { AuthStore } from './store/auth/auth.store';
import { SettingsStore } from './store/settings/settings.store';
import { UserConfigService } from './services/user-config.service';
import { MapService, tileRiskIds } from './services/map.service';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { Router } from '@angular/router';
import { waitForSignalValue } from './shared/util/helpers/signal.helper';
import { LOCAL_STORAGE_KEYS } from './constants/storage.const';
import { ReauthService, TOKEN_TIMEOUT_MS } from './services/reauth.service';

const MEMBERSHIPS_TIMEOUT_MS = 15000;

/**
 * A factory function returning a promise to preload the languages assets
 * before starting the Angular app, used as an APP_INITIALIZER provider
 * @param translocoService used to set active language and load language asset
 * @returns a promise resolving the translation asset.
 */
export function preloadLanguage(translocoService: TranslocoService) {
  return () => {
    const regex = /language\/([^\\/?\s]+)/;
    const match = window.location.href.match(regex);

    const persistedLang = localStorage.getItem(
      LOCAL_STORAGE_KEYS.TRANSLOCO_LANG
    );

    let language = 'engb';
    if (match && match.length > 1) {
      // URL language parameter takes precedence
      language = match[1];
    } else if (persistedLang) {
      language = persistedLang;
    }

    translocoService.setActiveLang(language);
    return lastValueFrom(translocoService.load(language));
  };
}

export function initializeApp(injector: Injector): () => Promise<unknown> {
  const auth0Service = injector.get<Auth0Service>(Auth0Service);
  const authStore = injector.get(AuthStore);
  const settingsStore = injector.get(SettingsStore);
  const userConfigService = injector.get(UserConfigService);
  const mapService = injector.get(MapService);
  const router = injector.get(Router);
  const reauthService = injector.get(ReauthService);
  const ngDocument = injector.get(DOCUMENT);
  if (window.location.href.includes('error=')) {
    return handleSupplierPortalSsoError(auth0Service, router);
  }
  ngDocument.documentElement.classList.add('show-loader');
  return () =>
    lastValueFrom(
      auth0Service.isAuthenticated$.pipe(
        timeout(5000),
        take(1),
        catchError(() => of(false)),
        switchMap((isAuthenticated) => {
          if (!isAuthenticated) {
            return of(false);
          }
          return loadAuth0Token(authStore, auth0Service).pipe(
            switchMap((token) => {
              if (!token) {
                return reauthService.reauthenticate();
              }
              return loadAuth0User(authStore, auth0Service).pipe(
                switchMap(() => loadUserTheme(authStore, userConfigService)),
                switchMap((theme) =>
                  loadMemberships(authStore, theme, injector)
                ),
                switchMap(() =>
                  loadSettings(
                    authStore,
                    settingsStore,
                    router,
                    reauthService,
                    injector
                  )
                ),
                tap(() => primeTileUrls(settingsStore, mapService))
              );
            })
          );
        })
      )
    );
}

function loadAuth0Token(
  authStore: InstanceType<typeof AuthStore>,
  auth0Service: Auth0Service
) {
  return auth0Service.getAccessTokenSilently().pipe(
    timeout(TOKEN_TIMEOUT_MS),
    take(1),
    catchError(() => of(null)),
    tap((res) => authStore.setToken(res))
  );
}

function loadAuth0User(
  authStore: InstanceType<typeof AuthStore>,
  auth0Service: Auth0Service
) {
  return auth0Service.user$.pipe(
    timeout(5000),
    take(1),
    catchError(() => of(null)),
    tap((res) => authStore.setUser(res ?? null))
  );
}

function loadUserTheme(
  authStore: InstanceType<typeof AuthStore>,
  userConfigService: UserConfigService
) {
  const user = authStore.user();
  if (user?.org_name) {
    authStore.setTenantId(user.org_name);
    return userConfigService.setTheme(user.org_name);
  }
  return of(null);
}

/**
 * Loads the user's memberships and waits for the request to finish, whether
 * it returns a list (possibly empty) or fails. Without a theme the tenant's
 * configuration did not load, so memberships are not requested and stay null.
 */
function loadMemberships(
  authStore: InstanceType<typeof AuthStore>,
  theme: unknown,
  injector: Injector
) {
  const themeWithColors = theme as { colors?: unknown } | null;
  if (!themeWithColors?.colors) {
    return of(null);
  }

  authStore.loadMemberships();
  const isLoadingMemberships = computed(
    () => authStore.isLoading().memberships
  );
  return waitForSignalValue(
    isLoadingMemberships,
    (isLoading) => !isLoading,
    'Timeout waiting for memberships:',
    true,
    injector,
    MEMBERSHIPS_TIMEOUT_MS
  );
}

/**
 * Loads settings for the selected membership. Memberships that did not load
 * (a failed or timed-out request, or missing tenant configuration) show an
 * error the user can retry; an empty list means the account is not linked to
 * a supplier for this company, which retrying will not fix. Memberships that
 * failed because the session could not be authenticated leave the user on the
 * reauthentication path instead; memberships that loaded end its retry window.
 */
function loadSettings(
  authStore: InstanceType<typeof AuthStore>,
  settingsStore: InstanceType<typeof SettingsStore>,
  router: Router,
  reauthService: ReauthService,
  injector: Injector
) {
  if (reauthService.isReauthenticating()) {
    return reauthService.reauthenticate();
  }
  const memberships = authStore.memberships();
  const tenantId = authStore.tenantId() ?? '';
  if (!memberships) {
    router.navigate(['error'], {
      queryParams: {
        detail: 'TOAST.MEMBERSHIPS_LOAD_FAILED',
        tenantId,
        retry: 'true'
      }
    });
    return of(null);
  }
  // The API accepted the session, so a later failure may redirect again.
  reauthService.clearRetryWindow();
  if (memberships.length === 0) {
    router.navigate(['error'], {
      queryParams: { detail: 'TOAST.NO_MEMBERSHIPS_FOUND', tenantId }
    });
    return of(null);
  }

  const portal = authStore.selectedPortal();
  const supplierId = authStore.selectedSupplier()?.id ?? null;
  settingsStore.loadSettings({ portal, supplierId });
  return waitForSignalValue(
    settingsStore.settings,
    (settings) => !!settings,
    'Timeout waiting for settings:',
    null,
    injector
  );
}

function primeTileUrls(
  settingsStore: InstanceType<typeof SettingsStore>,
  mapService: MapService
): void {
  const overlapRiskIds = tileRiskIds(settingsStore.settings()?.risks ?? []);
  if (overlapRiskIds.length === 0) return;
  mapService.getTileUrls(overlapRiskIds).subscribe({
    error: (err: unknown) => console.warn('Tile URL priming failed:', err)
  });
}

function handleSupplierPortalSsoError(
  auth0Service: Auth0Service,
  router: Router
) {
  return () =>
    lastValueFrom(
      auth0Service.error$.pipe(
        timeout(5000),
        filter((error) => !!error),
        take(1),
        switchMap((error) => {
          const errorDescription = error as unknown as {
            error_description: string;
          };
          if (errorDescription) {
            router.navigate(['error'], {
              queryParams: { detail: errorDescription }
            });
          }
          return of(false);
        })
      )
    );
}
